import type { UserLike } from '@/Ship/Helpers/fieldViews'

import type { UserHistoryEventCode } from './UserHistoryEvent'

/**
 * Права картки співробітника — `_permissions` у відповіді з `?permissions=true`
 * (`SPEC-005` §3.2). Фронт показує дії лише за ними, ролей сам не питає
 * (`SPEC-003` §1.3).
 */
export interface UserPermissions {
    read?: boolean
    update?: boolean
    /** Ролі: супер адміністратор, і не у власній картці */
    update_access?: boolean
    /** Активність: супер адміністратор, і не на собі (ВП-8) */
    update_status?: boolean
    /** Пароль Google Workspace: супер адміністратор — будь-кому, адміністратор — собі */
    update_gmail_app_password?: boolean
    /** Телефон-логін, телефони, відділи, сортування (ВП-5) */
    update_managed?: boolean
}

/** Значення картки так, як їх тримає форма (лише те, що читає фронт). */
export interface UserCardValues {
    uuid?: string
    '@id'?: string
    status?: string
    roles?: unknown
    hasGmailAppPassword?: boolean
    _permissions?: UserPermissions
    [key: string]: unknown
}

/** Те, що шаблон картки бере з ядрового `formContext`. */
export interface UserFormContextLike {
    isEditable?: boolean
    values?: UserCardValues
    setInitialValue?: (path: string, value: unknown) => void
    setValue?: (path: string, value: unknown) => void
}

/** Те, що шаблон картки бере з ядрового `crudContext`. */
export interface UserCrudContextLike {
    resource?: { key?: string; fields?: Record<string, unknown> }
    listCrudContext?: { listContext?: { actions?: { load?: (force?: boolean) => void } } }
}

/** Подія історії співробітника (`GET /services/users/{uuid}/history`). */
export interface UserHistoryItem {
    uuid?: string
    event: UserHistoryEventCode | string
    /**
     * `{ old, new }` — підписи «було» і «стало»; для ролей і відділів ще
     * `added` / `removed` (`Ship/Services/UserHistoryService`).
     */
    payload?: {
        old?: unknown
        new?: unknown
        added?: unknown
        removed?: unknown
        [key: string]: unknown
    } | null
    changedBy?: UserLike | null
    occurredAt?: string
}

/**
 * Довжина пароля додатку Google Workspace — копія беківської
 * `UserEntity::GMAIL_APP_PASSWORD_LENGTH`. Пару звіряє вартовий «константи,
 * скопійовані з беку», а число в підказці `team.user.gmail` — вартовий
 * «числа в підказках».
 */
export const GMAIL_APP_PASSWORD_LENGTH = 16

/** Поля, якими керує лише супер адміністратор (ВП-5, `SPEC-005` §9.2). */
export const MANAGED_FIELDS = ['phone', 'phones', 'departments', 'sorting', 'externalCode'] as const

/** Поля доступу: ролі змінює лише той, у кого є `update_access`. */
export const ACCESS_FIELDS = ['roles', 'accessProperties'] as const
